import { useMemo, useState } from "react";
import { useI18n } from "../../i18n";
import { cn } from "../cn";
import { useOnboarding } from "./useOnboarding";
import { computeSteps, doneCount, STEP_COUNT, type StepModel, type StepStatus } from "./steps";
import type { OnboardingPersona } from "../../lib/onboarding-api";
import { useOwnerTerritories } from "../dashboard/useOwnerTerritories";
import { useTerritoryPreviews } from "../dashboard/useTerritoryPreviews";

const go = (hash: string) => {
  window.location.hash = hash;
};

const STATUS_KEY: Record<StepStatus, string> = {
  done: "onboarding.progress.statusDone",
  current: "onboarding.progress.statusNow",
  todo: "onboarding.progress.statusNext",
  locked: "onboarding.progress.statusLocked",
};

/**
 * OnboardingRoute — Macro #27 guided-setup CONTROL CENTER (`#onboarding`).
 *
 * Orchestrates the real product surfaces (claim, Territory Identity, Canvas,
 * Publish, Explore) — it never re-implements them. Progress is derived from
 * canonical state; the client cannot fake completion.
 */
export function OnboardingRoute() {
  const { t } = useI18n();
  const onb = useOnboarding();
  const steps = useMemo(() => computeSteps(onb.state, onb.canonical), [onb.state, onb.canonical]);
  const done = doneCount(steps);

  return (
    <div data-testid="onboarding-route" className="w-full h-full overflow-y-auto bg-obsidian">
      <div className="mx-auto w-[min(920px,100%)] px-5 md:px-8 py-10 md:py-14">
        <header className="flex flex-col gap-3">
          <span className="flex items-center gap-3">
            <span aria-hidden className="h-px w-8 bg-neon-teal/60" />
            <span className="font-mono text-[10.5px] tracking-[0.28em] uppercase text-neon-teal">
              {t("onboarding.eyebrow")}
            </span>
          </span>
          <h1 className="m-0 font-display text-4xl sm:text-5xl font-semibold tracking-[-0.02em] text-text-primary">
            {t("onboarding.title")}
          </h1>
          <p className="m-0 max-w-[680px] font-display text-sm md:text-base leading-[1.65] text-text-secondary">
            {t("onboarding.intro")}
          </p>
        </header>

        {onb.status === "auth_required" && <AuthGate />}
        {onb.status === "loading" && (
          <div data-testid="onboarding-loading" className="mt-10 rounded-lg border border-border-soft bg-panel p-8 font-mono text-[13px] text-text-muted">
            {t("onboarding.loading")}
          </div>
        )}
        {onb.status === "error" && (
          <div data-testid="onboarding-error" className="mt-10 rounded-lg border border-border-soft bg-panel p-8">
            <p className="m-0 font-display text-[15px] text-text-primary">{t("onboarding.error.title")}</p>
            <p className="mt-2 text-[12px] text-text-muted">{t("onboarding.error.body")}</p>
            <button data-testid="onboarding-retry" onClick={onb.refresh} className="facet-btn mt-5 h-9 px-4 text-[11px] font-semibold tracking-[0.16em]">
              {t("onboarding.error.retry")}
            </button>
          </div>
        )}

        {onb.status === "ready" && (
          <>
            <div className="mt-8 flex flex-wrap items-center justify-between gap-4">
              <ProgressBar done={done} total={STEP_COUNT} />
              <button
                data-testid="onboarding-restart"
                onClick={() => {
                  if (window.confirm(t("onboarding.restartConfirm"))) void onb.restart();
                }}
                disabled={onb.mutating}
                className="inline-flex items-center gap-2 h-8 px-3 no-underline font-mono text-[10px] font-medium tracking-[0.16em] uppercase text-text-secondary neon-glass pill-ghost hover:text-text-primary disabled:opacity-40"
              >
                {t("onboarding.restart")}
              </button>
            </div>

            {onb.canonical?.ownership_lost && (
              <div data-testid="onboarding-ownership-lost" className="mt-5 rounded-lg border border-[color:var(--neon-coral-ring)] bg-panel p-4 text-[12px] text-text-secondary">
                {t("onboarding.ownershipLost")}
              </div>
            )}

            <div className="mt-6 space-y-4">
              {steps.map((s) => (
                <StepCard key={s.id} step={s} onb={onb} />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function AuthGate() {
  const { t } = useI18n();
  return (
    <div data-testid="onboarding-auth-required" className="mt-10 rounded-lg border border-border-soft bg-panel p-8">
      <p className="m-0 font-display text-[15px] text-text-primary">{t("onboarding.authRequired.title")}</p>
      <p className="mt-2 text-[12px] text-text-muted max-w-[520px]">{t("onboarding.authRequired.body")}</p>
      <a
        data-testid="onboarding-signin-link"
        href="#auth"
        className="mt-5 inline-flex items-center gap-2 h-9 px-4 no-underline font-mono text-[11px] font-medium tracking-[0.16em] uppercase text-text-inverse bg-text-primary rounded-md hover:opacity-90"
      >
        {t("onboarding.authRequired.cta")}
      </a>
    </div>
  );
}

function ProgressBar({ done, total }: { done: number; total: number }) {
  const { t } = useI18n();
  const pct = Math.round((done / total) * 100);
  return (
    <div data-testid="onboarding-progress" className="min-w-[220px] flex-1 max-w-[420px]">
      <div className="flex items-center justify-between">
        <span className="font-mono text-[10px] tracking-[0.16em] uppercase text-text-muted">
          {t("onboarding.progress.complete", { done, total })}
        </span>
        <span className="font-mono text-[10px] text-neon-teal tabular-nums">{pct}%</span>
      </div>
      <div className="mt-2 h-1.5 w-full rounded-pill bg-white/10 overflow-hidden">
        <div className="h-full rounded-pill bg-neon-teal transition-[width] duration-med" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function StatusPill({ status }: { status: StepStatus }) {
  const { t } = useI18n();
  return (
    <span
      data-testid="onboarding-step-status"
      data-status={status}
      className={cn(
        "shrink-0 inline-flex items-center h-6 px-2.5 rounded-sm font-mono text-[9.5px] font-semibold uppercase tracking-[0.14em] whitespace-nowrap",
        status === "done" && "text-neon-teal border border-border-accent bg-neon-teal-soft",
        status === "current" && "text-obsidian bg-neon-teal",
        status === "todo" && "text-text-secondary border border-border-soft",
        status === "locked" && "text-text-muted border border-border-soft opacity-70",
      )}
    >
      {t(STATUS_KEY[status])}
    </span>
  );
}

function StepCard({ step, onb }: { step: StepModel; onb: ReturnType<typeof useOnboarding> }) {
  const { t } = useI18n();
  const titleKey = `onboarding.steps.${step.id}.title`;
  const bodyKey = `onboarding.steps.${step.id}.body`;
  return (
    <section
      data-testid={`onboarding-step-${step.id}`}
      data-step-status={step.status}
      className={cn(
        "rounded-lg border bg-panel p-5 md:p-6 transition-colors duration-med",
        step.status === "current" ? "border-border-accent shadow-glow-teal-sm" : "border-border-soft",
        step.status === "locked" && "opacity-70",
      )}
    >
      <div className="flex items-start gap-4">
        <span
          aria-hidden
          className={cn(
            "shrink-0 mt-0.5 inline-flex h-8 w-8 items-center justify-center rounded-md font-mono text-[12px] font-semibold tabular-nums",
            step.done ? "bg-neon-teal-soft text-neon-teal border border-border-accent" : "bg-white/5 text-text-secondary border border-border-soft",
          )}
        >
          {step.done ? "✓" : step.index}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="font-mono text-[9.5px] tracking-[0.2em] uppercase text-text-muted">
                {t("onboarding.progress.label", { n: step.index, total: STEP_COUNT })}
              </div>
              <h2 className="mt-1 font-display text-[16px] md:text-lg font-semibold tracking-[-0.01em] text-text-primary">
                {t(titleKey)}
              </h2>
            </div>
            <StatusPill status={step.status} />
          </div>
          <p className="mt-2 font-display text-[13px] leading-[1.6] text-text-secondary">{t(bodyKey)}</p>

          {!step.locked && (
            <div className="mt-4">
              {step.id === "persona" && <PersonaBody onb={onb} />}
              {step.id === "territory" && <TerritoryBody onb={onb} />}
              {step.id === "start" && <StartBody onb={onb} />}
              {step.id === "build" && <BuildBody onb={onb} />}
              {step.id === "publish" && <PublishBody onb={onb} />}
              {step.id === "launch" && <LaunchBody onb={onb} />}
            </div>
          )}
          {step.locked && step.id === "launch" && (
            <p data-testid="onboarding-launch-locked" className="mt-3 font-mono text-[11px] text-text-muted">
              {t("onboarding.steps.launch.locked")}
            </p>
          )}
        </div>
      </div>
    </section>
  );
}

// ── Step 1 — persona ─────────────────────────────────────────────────────────
function PersonaBody({ onb }: { onb: ReturnType<typeof useOnboarding> }) {
  const { t } = useI18n();
  const current = onb.state?.persona ?? null;
  const choose = (persona: OnboardingPersona) => {
    const nextStep = Math.max(onb.state?.current_step ?? 1, 2);
    void onb.patch({ persona, current_step: nextStep });
  };
  const options: OnboardingPersona[] = ["creator", "retail"];
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      {options.map((p) => {
        const selected = current === p;
        return (
          <button
            key={p}
            data-testid={`onboarding-persona-${p}`}
            data-selected={selected}
            onClick={() => choose(p)}
            disabled={onb.mutating}
            className={cn(
              "text-left rounded-md border p-4 transition-colors duration-fast disabled:opacity-50",
              selected ? "border-border-accent bg-neon-teal-soft/40 shadow-glow-teal-sm" : "border-border-soft hover:border-border-accent hover:bg-elevated",
            )}
          >
            <div className="flex items-center justify-between gap-2">
              <span className="font-display text-[14px] font-semibold text-text-primary">
                {t(`onboarding.personas.${p}.label`)}
              </span>
              {selected && (
                <span className="font-mono text-[9px] tracking-[0.14em] uppercase text-neon-teal">
                  {t("onboarding.personas.selected")}
                </span>
              )}
            </div>
            <p className="mt-1.5 font-display text-[12px] leading-[1.55] text-text-secondary">
              {t(`onboarding.personas.${p}.desc`)}
            </p>
          </button>
        );
      })}
    </div>
  );
}

// ── Step 2 — territory ────────────────────────────────────────────────────────
function TerritoryBody({ onb }: { onb: ReturnType<typeof useOnboarding> }) {
  const { t } = useI18n();
  const territoriesState = useOwnerTerritories();
  const list = territoriesState.status === "resolved" ? territoriesState.territories : [];
  const previews = useTerritoryPreviews(list);
  const selectedId = onb.state?.selected_territory ?? null;

  const select = (identityId: string) => {
    void onb.patch({ selected_territory: identityId, current_step: Math.max(onb.state?.current_step ?? 2, 3) });
  };

  if (territoriesState.status === "loading") {
    return <p data-testid="onboarding-territory-loading" className="font-mono text-[12px] text-text-muted">{t("onboarding.steps.territory.loading")}</p>;
  }
  if (territoriesState.status === "unavailable") {
    return <p data-testid="onboarding-territory-unavailable" className="font-mono text-[12px] text-text-muted">{t("onboarding.steps.territory.unavailable")}</p>;
  }
  if (territoriesState.status === "empty" || list.length === 0) {
    return (
      <div data-testid="onboarding-territory-empty" className="rounded-md border border-dashed border-border-soft p-4">
        <p className="m-0 text-[12.5px] text-text-secondary">{t("onboarding.steps.territory.needLand")}</p>
        <a href="#purchase" data-testid="onboarding-get-land" className="facet-btn mt-3 h-9 px-4 no-underline text-[11px] font-semibold tracking-[0.16em]">
          {t("onboarding.steps.territory.getLand")}
        </a>
      </div>
    );
  }

  return (
    <div className="space-y-2.5">
      <div className="font-mono text-[10px] tracking-[0.16em] uppercase text-text-muted">{t("onboarding.steps.territory.pick")}</div>
      {list.map((terr, i) => {
        const preview = previews.get(terr.key);
        const identityId = preview?.identityId ?? null;
        const label = preview?.displayName?.trim() ? preview.displayName : `Territory ${String(i + 1).padStart(2, "0")}`;
        const isSelected = identityId != null && identityId === selectedId;
        return (
          <div
            key={terr.key}
            data-testid="onboarding-territory-row"
            data-selected={isSelected}
            className={cn(
              "flex items-center justify-between gap-3 rounded-md border p-3.5",
              isSelected ? "border-border-accent bg-neon-teal-soft/30" : "border-border-soft",
            )}
          >
            <div className="min-w-0">
              <div className="truncate font-display text-[13.5px] font-semibold text-text-primary">{label}</div>
              <div className="mt-0.5 font-mono text-[10px] text-text-muted">
                {terr.area.toLocaleString()} u² · {terr.unitCount} · {terr.publishable ? "Publishable" : "Not publishable"}
              </div>
            </div>
            {identityId ? (
              isSelected ? (
                <span data-testid="onboarding-territory-selected" className="shrink-0 inline-flex items-center h-7 px-3 rounded-sm font-mono text-[9.5px] font-semibold uppercase tracking-[0.14em] text-neon-teal border border-border-accent bg-neon-teal-soft">
                  {t("onboarding.steps.territory.selectedLabel")}
                </span>
              ) : (
                <button
                  data-testid="onboarding-territory-select"
                  onClick={() => select(identityId)}
                  disabled={onb.mutating}
                  className="shrink-0 facet-btn h-8 px-4 text-[10.5px] font-semibold tracking-[0.16em] disabled:opacity-50"
                >
                  {t("onboarding.actions.select")}
                </button>
              )
            ) : (
              <a href="#dashboard" data-testid="onboarding-territory-name" className="shrink-0 inline-flex items-center h-8 px-3 no-underline font-mono text-[10px] tracking-[0.14em] uppercase text-text-secondary neon-glass pill-ghost hover:text-text-primary">
                {t("onboarding.steps.territory.nameIt")}
              </a>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── Step 3 — choose how to start ───────────────────────────────────────────────
function StartBody({ onb }: { onb: ReturnType<typeof useOnboarding> }) {
  const { t } = useI18n();
  const id = onb.canonical?.selected_territory?.identity_id ?? null;
  const open = () => {
    void onb.patch({ current_step: Math.max(onb.state?.current_step ?? 3, 4) });
    if (id) go(`#canvas/${id}`);
  };
  return (
    <div>
      <div className="flex flex-wrap gap-3">
        <button data-testid="onboarding-start-blank" onClick={open} disabled={!id} className="facet-btn h-9 px-4 text-[11px] font-semibold tracking-[0.16em] disabled:opacity-40">
          {t("onboarding.steps.start.blank")}
        </button>
        <button data-testid="onboarding-start-template" onClick={open} disabled={!id} className="inline-flex items-center h-9 px-4 rounded-md border border-neon-teal text-neon-teal font-mono text-[11px] uppercase tracking-[0.16em] hover:bg-neon-teal-soft disabled:opacity-40">
          {t("onboarding.steps.start.template")}
        </button>
      </div>
      <p className="mt-2.5 font-mono text-[11px] text-text-muted">{t("onboarding.steps.start.hint")}</p>
    </div>
  );
}

// ── Step 4 — build ─────────────────────────────────────────────────────────────
function BuildBody({ onb }: { onb: ReturnType<typeof useOnboarding> }) {
  const { t } = useI18n();
  const sel = onb.canonical?.selected_territory ?? null;
  const id = sel?.identity_id ?? null;
  const draftSaved = (sel?.draft_revision ?? 0) > 0;
  const open = () => {
    void onb.patch({ current_step: Math.max(onb.state?.current_step ?? 4, 4) });
    if (id) go(`#canvas/${id}`);
  };
  return (
    <div className="flex flex-wrap items-center gap-3">
      <button data-testid="onboarding-open-canvas" onClick={open} disabled={!id} className="facet-btn h-9 px-4 text-[11px] font-semibold tracking-[0.16em] disabled:opacity-40">
        {t("onboarding.steps.build.open")}
      </button>
      <span
        data-testid="onboarding-build-status"
        data-draft-saved={draftSaved}
        className={cn("font-mono text-[11px] tracking-[0.06em]", draftSaved ? "text-neon-teal" : "text-text-muted")}
      >
        {draftSaved ? `✓ ${t("onboarding.steps.build.draftSaved")}` : t("onboarding.steps.build.noDraft")}
      </span>
    </div>
  );
}

// ── Step 5 — publish ────────────────────────────────────────────────────────────
function PublishBody({ onb }: { onb: ReturnType<typeof useOnboarding> }) {
  const { t } = useI18n();
  const sel = onb.canonical?.selected_territory ?? null;
  const hasPub = Boolean(sel?.has_active_publication);
  return (
    <div className="flex flex-wrap items-center gap-3">
      <a href="#dashboard" data-testid="onboarding-open-publish" className="facet-btn h-9 px-4 no-underline text-[11px] font-semibold tracking-[0.16em]">
        {t("onboarding.steps.publish.open")}
      </a>
      <span
        data-testid="onboarding-publish-status"
        data-published={hasPub}
        className={cn("font-mono text-[11px] tracking-[0.06em]", hasPub ? "text-neon-teal" : "text-text-muted")}
      >
        {hasPub ? `✓ ${t("onboarding.steps.publish.published")}` : t("onboarding.steps.publish.notReady")}
      </span>
    </div>
  );
}

// ── Step 6 — launch & grow ────────────────────────────────────────────────────
function LaunchBody({ onb }: { onb: ReturnType<typeof useOnboarding> }) {
  const { t } = useI18n();
  const id = onb.canonical?.selected_territory?.identity_id ?? null;
  const [copied, setCopied] = useState(false);
  const publicHash = id ? `#p/${id}` : "#explore";
  const copyLink = async () => {
    if (!id) return;
    const url = `${window.location.origin}${window.location.pathname}#p/${id}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard unavailable — the public link is still shown via the View button */
    }
  };
  return (
    <div className="flex flex-wrap items-center gap-3">
      <a href={publicHash} data-testid="onboarding-view-public" className="facet-btn h-9 px-4 no-underline text-[11px] font-semibold tracking-[0.16em]">
        {t("onboarding.steps.launch.viewPublic")}
      </a>
      <a href="#explore" data-testid="onboarding-explore-link" className="inline-flex items-center h-9 px-4 no-underline rounded-md border border-border-soft text-text-secondary font-mono text-[11px] uppercase tracking-[0.16em] hover:text-text-primary hover:border-border-accent">
        {t("onboarding.steps.launch.explore")}
      </a>
      <button data-testid="onboarding-copy-link" onClick={copyLink} disabled={!id} className="inline-flex items-center h-9 px-4 rounded-md border border-border-soft text-text-secondary font-mono text-[11px] uppercase tracking-[0.16em] hover:text-text-primary hover:border-border-accent disabled:opacity-40">
        {copied ? t("onboarding.steps.launch.copied") : t("onboarding.steps.launch.copyLink")}
      </button>
    </div>
  );
}
